import type { Mesure, Via } from "./contract";
import { normaliserCode } from "./moteur/codes";
import { choisir, demarrer, normaliserReponse, type Refus, repondre, scene } from "./moteur/moteur";
import { decoder, encoder, type RefusSauvegarde } from "./moteur/sauvegarde";
import type { ChoixCompile, Etat, Livre, TypeFin } from "./moteur/types";

/**
 * Une partie, vue du telephone : l'etat du moteur et de quoi mesurer sans rien savoir du
 * lecteur. Fonctions pures sur le livre, l'horloge passee en argument : le magasin (`jeu.ts`)
 * range la partie et envoie les mesures, ces fonctions decident.
 */

/** Le dernier choix applique : il redit au lecteur ou il en est, et reconnait un double scan. */
export interface DernierChoix {
  code: string;
  libelle: string;
  page: number;
}

/** Ce que le navigateur garde d'une partie (`rangement.ts`). */
export interface Partie {
  v: 1;
  /** Identifiant du livre : une partie d'un autre livre ne se lit pas. */
  livre: string;
  etat: Etat;
  /** Debut de la partie, en millisecondes : la duree de la fin en est tiree. */
  debut: number;
  /** Choix appliques, enigmes comprises. */
  coups: number;
  /** Codes refuses pendant la partie. */
  refus: number;
  /** Codes tapes a la main plutot que scannes. */
  saisis: number;
  dernier: DernierChoix | null;
  /** La fin a deja ete mesuree : une partie ne compte qu'une fin. */
  finMesuree: boolean;
}

/** Une mesure telle que la partie la decide ; le magasin y ajoute l'identifiant du livre. */
type SansLivre<T> = T extends unknown ? Omit<T, "livre"> : never;
export type MesureDePartie = SansLivre<Mesure>;

export interface Changements {
  /** Libelles des indices gagnes, dans l'ordre du livre. */
  gagnes: string[];
  perdus: string[];
  jauges: { libelle: string; avant: number; apres: number; max: number }[];
}

/** Ce qu'un code lu veut dire, avant toute action. */
export type Lecture =
  | { type: "choix"; code: string; libelle: string; page: number; seul: boolean }
  | { type: "enigme"; code: string; libelle: string; question: string; page: number }
  | { type: "deja-fait"; libelle: string; page: number }
  | { type: "refus"; code: string; refus: Refus; page: number; recommencer: boolean };

/** Un choix applique : la partie a ranger, la page a lire et ce qui a change dans le carnet. */
export interface Coup {
  partie: Partie;
  page: number;
  libelle: string;
  changements: Changements;
  fin: TypeFin | null;
  mesures: MesureDePartie[];
}

export type Issue = { ok: true; coup: Coup } | { ok: false; refus: Refus; page: number };

export function nouvellePartie(livre: Livre, maintenant: number): Partie {
  return {
    v: 1,
    livre: livre.id,
    etat: demarrer(livre),
    debut: maintenant,
    coups: 0,
    refus: 0,
    saisis: 0,
    dernier: null,
    finMesuree: false,
  };
}

/** La page ou en est la partie ; la page de depart quand il n'y en a pas. */
export function pageCourante(livre: Livre, partie: Partie | null): number {
  const id = partie?.etat.scene ?? livre.depart;
  return livre.scenes.find((candidate) => candidate.id === id)?.page ?? 1;
}

export function finDeLaPartie(livre: Livre, partie: Partie | null): TypeFin | null {
  if (!partie) return null;
  return livre.scenes.find((candidate) => candidate.id === partie.etat.scene)?.fin ?? null;
}

function choixDeLaScene(livre: Livre, etat: Etat, code: string): ChoixCompile | undefined {
  return scene(livre, etat.scene).choix.find((choix) => choix.code === code);
}

/** Le code serait-il accepte sur la page 1 d'une partie neuve ? */
function accepteAuDepart(livre: Livre, code: string): boolean {
  const essai = choisir(livre, demarrer(livre), code);
  return essai.ok || essai.refus === "enigme-attendue";
}

/**
 * Ce que veut dire un code lu sur la page, sans rien appliquer. Sans partie, le lecteur est en
 * page 1 : un code de la page 1 commence la partie, tout autre est refuse.
 */
export function lire(livre: Livre, partie: Partie | null, saisie: string): Lecture {
  const code = normaliserCode(saisie);
  const etat = partie?.etat ?? demarrer(livre);
  const essai = choisir(livre, etat, code);
  if (essai.ok || essai.refus === "enigme-attendue") {
    const courante = scene(livre, etat.scene);
    const choix = choixDeLaScene(livre, etat, code) as ChoixCompile;
    if (choix.enigme) {
      return {
        type: "enigme",
        code,
        libelle: choix.libelle,
        question: choix.enigme.question ?? "",
        page: courante.page,
      };
    }
    return {
      type: "choix",
      code,
      libelle: choix.libelle,
      page: courante.page,
      seul: courante.choix.length === 1,
    };
  }
  const dejaVu =
    (essai.refus === "pas-sur-cette-page" || essai.refus === "partie-finie") &&
    partie?.dernier?.code === code;
  if (dejaVu && partie?.dernier) {
    return { type: "deja-fait", libelle: partie.dernier.libelle, page: essai.page };
  }
  // Le lecteur a peut-etre recommence le livre sans le dire au telephone : on le lui propose.
  const recommencer =
    partie !== null &&
    (essai.refus === "pas-sur-cette-page" || essai.refus === "partie-finie") &&
    accepteAuDepart(livre, code);
  return { type: "refus", code, refus: essai.refus, page: essai.page, recommencer };
}

/** Ce qui a change dans le carnet entre deux etats. */
export function changements(livre: Livre, avant: Etat, apres: Etat): Changements {
  return {
    gagnes: livre.indices
      .filter((indice) => apres.indices.includes(indice.id) && !avant.indices.includes(indice.id))
      .map((indice) => indice.libelle),
    perdus: livre.indices
      .filter((indice) => avant.indices.includes(indice.id) && !apres.indices.includes(indice.id))
      .map((indice) => indice.libelle),
    jauges: livre.jauges
      .filter((jauge) => (avant.jauges[jauge.id] ?? 0) !== (apres.jauges[jauge.id] ?? 0))
      .map((jauge) => ({
        libelle: jauge.libelle,
        avant: avant.jauges[jauge.id] ?? 0,
        apres: apres.jauges[jauge.id] ?? 0,
        max: jauge.max,
      })),
  };
}

function conclure(
  livre: Livre,
  existait: boolean,
  base: Partie,
  code: string,
  etat: Etat,
  via: Via,
  maintenant: number,
  avant: MesureDePartie[],
): Coup {
  const choix = choixDeLaScene(livre, base.etat, code) as ChoixCompile;
  const arrivee = scene(livre, etat.scene);
  const fin = arrivee.fin ?? null;
  const partie: Partie = {
    ...base,
    etat,
    coups: base.coups + 1,
    saisis: base.saisis + (via === "saisie" ? 1 : 0),
    dernier: { code, libelle: choix.libelle, page: arrivee.page },
    finMesuree: base.finMesuree || fin !== null,
  };
  const mesures: MesureDePartie[] = existait ? [] : [{ quoi: "partie" }];
  mesures.push(...avant, { quoi: "choix", scene: arrivee.id, page: arrivee.page, via });
  if (fin !== null && !base.finMesuree) {
    mesures.push({
      quoi: "fin",
      type: fin,
      minutes: Math.max(0, Math.round((maintenant - base.debut) / 60_000)),
      coups: partie.coups,
      refus: partie.refus,
      saisis: partie.saisis,
    });
  }
  return {
    partie,
    page: arrivee.page,
    libelle: choix.libelle,
    changements: changements(livre, base.etat, etat),
    fin,
    mesures,
  };
}

/** Le lecteur confirme un choix ordinaire : la premiere suite dont la condition est vraie decide. */
export function jouerChoix(
  livre: Livre,
  partie: Partie | null,
  saisie: string,
  via: Via,
  maintenant: number,
): Issue {
  const code = normaliserCode(saisie);
  const base = partie ?? nouvellePartie(livre, maintenant);
  const resultat = choisir(livre, base.etat, code);
  if (!resultat.ok) return { ok: false, refus: resultat.refus, page: resultat.page };
  return {
    ok: true,
    coup: conclure(livre, partie !== null, base, code, resultat.etat, via, maintenant, []),
  };
}

/** Le lecteur repond a une enigme ; une reponse vide vaut renoncer, donc l'issue fausse. */
export function jouerReponse(
  livre: Livre,
  partie: Partie | null,
  saisie: string,
  reponse: string,
  via: Via,
  maintenant: number,
): Issue {
  const code = normaliserCode(saisie);
  const base = partie ?? nouvellePartie(livre, maintenant);
  const resultat = repondre(livre, base.etat, code, reponse);
  if (!resultat.ok) return { ok: false, refus: resultat.refus, page: resultat.page };
  const enigme = choixDeLaScene(livre, base.etat, code)?.enigme;
  // Meme comparaison que le moteur, pour mesurer sans jamais envoyer la reponse elle-meme.
  const lue = normaliserReponse(reponse);
  const juste =
    lue !== "" && (enigme?.solutions ?? []).some((solution) => normaliserReponse(solution) === lue);
  return {
    ok: true,
    coup: conclure(livre, partie !== null, base, code, resultat.etat, via, maintenant, [
      { quoi: "enigme", scene: base.etat.scene, juste, via },
    ]),
  };
}

/** Le code de sauvegarde de la partie, `XXXX-XXXX-X` ; `null` sans partie. */
export function codeDeSauvegarde(livre: Livre, partie: Partie | null): string | null {
  if (!partie) return null;
  try {
    return encoder(livre, partie.etat);
  } catch {
    // Un livre hors des limites du code (le verificateur l'interdit) : pas de code a donner.
    return null;
  }
}

/** Reprend une partie a partir d'un code de sauvegarde. */
export function reprendre(
  livre: Livre,
  saisie: string,
  maintenant: number,
): { ok: true; partie: Partie; page: number } | { ok: false; refus: RefusSauvegarde } {
  const lu = decoder(livre, saisie);
  if (!lu.ok) return lu;
  const arrivee = scene(livre, lu.etat.scene);
  return {
    ok: true,
    // Une partie reprise repart de zero pour les compteurs : seule la duree de cette reprise
    // sera mesuree. Une partie deja finie ne compte pas une seconde fin.
    partie: {
      ...nouvellePartie(livre, maintenant),
      etat: lu.etat,
      finMesuree: Boolean(arrivee.fin),
    },
    page: arrivee.page,
  };
}
